import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RoleGroup, RoleSpecialty, DoctorRank, Department } from '../types/user';
import apiService from '../services/api';

interface RolesState {
  roleGroups: RoleGroup[];
  roleSpecialties: RoleSpecialty[];
  doctorRanks: DoctorRank[];
  departments: Department[];
  loading: boolean;
  error: string | null;
}

const initialState: RolesState = {
  roleGroups: [],
  roleSpecialties: [],
  doctorRanks: [],
  departments: [],
  loading: false,
  error: null,
};

// Async thunks for fetching role data
export const fetchRoleGroups = createAsyncThunk(
  'roles/fetchRoleGroups',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getRoleGroups();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch role groups');
    }
  }
);

export const fetchRoleSpecialties = createAsyncThunk(
  'roles/fetchRoleSpecialties',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getRoleSpecialties();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch role specialties');
    }
  }
);

export const fetchDoctorRanks = createAsyncThunk(
  'roles/fetchDoctorRanks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getDoctorRanks();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch doctor ranks');
    }
  }
);

export const fetchDepartments = createAsyncThunk(
  'roles/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getDepartments();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch departments');
    }
  }
);

export const fetchAllRoleData = createAsyncThunk(
  'roles/fetchAllRoleData',
  async (_, { dispatch }) => {
    const [roleGroups, roleSpecialties, doctorRanks] = await Promise.all([
      dispatch(fetchRoleGroups()).unwrap(),
      dispatch(fetchRoleSpecialties()).unwrap(),
      dispatch(fetchDoctorRanks()).unwrap(),
    ]);
    
    return { roleGroups, roleSpecialties, doctorRanks };
  }
);

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRoles: (state) => {
      state.roleGroups = [];
      state.roleSpecialties = [];
      state.doctorRanks = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Role Groups
      .addCase(fetchRoleGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoleGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.roleGroups = action.payload;
      })
      .addCase(fetchRoleGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Role Specialties
      .addCase(fetchRoleSpecialties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoleSpecialties.fulfilled, (state, action) => {
        state.loading = false;
        state.roleSpecialties = action.payload;
      })
      .addCase(fetchRoleSpecialties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Doctor Ranks
      .addCase(fetchDoctorRanks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctorRanks.fulfilled, (state, action) => {
        state.loading = false;
        state.doctorRanks = action.payload;
      })
      .addCase(fetchDoctorRanks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Departments
      .addCase(fetchDepartments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.loading = false;
        state.departments = action.payload;
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch All Role Data
      .addCase(fetchAllRoleData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRoleData.fulfilled, (state, action) => {
        state.loading = false;
        // Individual thunks will have already updated the state
      })
      .addCase(fetchAllRoleData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch role data';
      });
  },
});

// Selectors
export const selectRoleGroups = (state: { roles: RolesState }) => state.roles.roleGroups;
export const selectRoleSpecialties = (state: { roles: RolesState }) => state.roles.roleSpecialties;
export const selectDoctorRanks = (state: { roles: RolesState }) => state.roles.doctorRanks;
export const selectRolesLoading = (state: { roles: RolesState }) => state.roles.loading;
export const selectRolesError = (state: { roles: RolesState }) => state.roles.error;

// Helper selectors
export const selectRoleSpecialtiesByGroup = (state: { roles: RolesState }, roleGroupId: number) => 
  state.roles.roleSpecialties.filter(specialty => specialty.role_group === roleGroupId);

export const selectRoleGroupById = (state: { roles: RolesState }, roleGroupId: number) =>
  state.roles.roleGroups.find(group => group.id === roleGroupId);

export const selectRoleSpecialtyById = (state: { roles: RolesState }, roleSpecialtyId: number) =>
  state.roles.roleSpecialties.find(specialty => specialty.id === roleSpecialtyId);

export const selectDoctorRankById = (state: { roles: RolesState }, doctorRankId: number) =>
  state.roles.doctorRanks.find(rank => rank.id === doctorRankId);

export const { clearError, clearRoles } = rolesSlice.actions;

export default rolesSlice.reducer;